"use client";

import Link from "next/link";
import { toast } from "sonner";
import { useState, createRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useRouter } from "next/navigation";
import { cn, withUrl } from "@/lib/utils";
import styles from "./ci-app-demo-request-form.module.css";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import PrimaryButton from "@/components/ui/primary-button";
import Testimonial from "@/components/ui/testimonial";
import LINKS from "@/lib/links";
import { submitDemoRequest } from "./ci-app-demo-request-form-actions";
import { useWindowSize } from "@/hooks/use-window-size";
import Ringer from "../ringer";
import ReCAPTCHA from "react-google-recaptcha";

export interface CIAppDemoRequestFormProps {
  className?: string;
  id?: string;
  heading?: string;
  subheading?: string;
  backgroundColor?: "transparent" | "orange-light";
  redirectUrl?: string;
  testimonials?: {
    image?: string;
    name: string;
    date: string;
    stars: number;
    text: string;
  }[];
}

// Define the form schema using Zod
const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  facilityName: z.string().min(1, "Facility name is required"),
  message: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

type FormSubmissionData = FormValues & {
  recaptchaToken: string;
};

const defaultTestimonials = [
  {
    image: undefined,
    name: "Channing House",
    date: "APR 2025",
    stars: 5,
    text: "Requesting shifts is so much faster compared to calling Care Indeed directly. I can easily check the Shift Calendar to see what's already been requested and what's coming up. Having everything in one place really streamlines communication and makes the whole process more efficient.",
  },
  {
    image: undefined,
    name: "The Forum",
    date: "APR 2025",
    stars: 5,
    text: "The biggest benefit of CI App is being able to post shifts without needing to contact anyone. I can view schedules instantly, which is especially helpful for auditing. It's a more efficient way to manage staffing, and it really streamlines the entire process.",
  },
];

const CIAppDemoRequestForm: React.FC<CIAppDemoRequestFormProps> = ({
  className,
  id,
  heading = "Trusted Partner to Care Facilities Like Yours",
  subheading = "Easily manage shifts, track time, and communicate with your care team—all from one app built for Facility Administrators.",
  backgroundColor = "orange-light",
  redirectUrl = "/thank-you",
  testimonials = defaultTestimonials,
}) => {
  const router = useRouter();
  const size = useWindowSize();
  const [isMounted, setIsMounted] = useState(false);
  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptchaChange = setRecaptchaToken;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      facilityName: "",
      message: "",
    },
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Handle form submission
  const onSubmit = async (data: FormValues) => {
    try {
      // Check if reCAPTCHA is completed
      if (!recaptchaToken) {
        toast.error("Please complete the CAPTCHA verification");
        // Set a non-field error to prevent form from being considered successful
        setError("root.recaptcha", {
          type: "manual",
          message: "Please complete the CAPTCHA verification",
        });
        return; // Exit early without setting success state
      }

      const submissionData: FormSubmissionData = {
        ...data,
        recaptchaToken,
      };

      const result = await submitDemoRequest(submissionData);

      if (result.status === "success") {
        toast.success(result.message || "Demo request submitted successfully!");
        // Reset form after successful submission
        reset();
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);

        // Construct the redirect URL with search parameters
        const redirectUrlWithParams = new URL(
          redirectUrl,
          window.location.origin
        );
        redirectUrlWithParams.searchParams.set(
          "name",
          `${data.firstName} ${data.lastName}`
        );
        redirectUrlWithParams.searchParams.set("email", data.email);

        // Redirect to the configured URL with search parameters
        router.push(redirectUrlWithParams.toString());
      } else {
        toast.error(
          result.message || "There was an error submitting your request"
        );
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Form submission error:", error);
    }
  };

  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        backgroundColor === "orange-light"
          ? "bg-primary-100"
          : "bg-transparent",
        className
      )}
    >
      <div className="pt-8 md:px-6 md:py-8 lg:mx-auto lg:max-w-lg lg:px-3 lg:py-10">
        <div className="flex flex-col gap-6 md:gap-10 lg:grid lg:grid-cols-2 lg:gap-14 lg:gap-y-0">
          <div className="order-1 flex h-fit flex-col px-4 md:px-0 lg:col-start-1 lg:row-start-1 lg:mb-0">
            <h2 className="text-center font-montserrat text-heading-4 text-neutral-600 md:text-left md:text-heading-3 lg:text-heading-2">
              {heading}
            </h2>
            <p className="mt-2 text-center font-montserrat text-subheading-3-400 text-neutral-500 md:text-left md:text-subheading-2 lg:mt-4">
              {subheading}
            </p>

            <div className="mt-6 flex flex-col">
              {/** Testimonials */}
              <div>
                <div className="flex flex-col gap-4">
                  {testimonials?.map((testimonial) => (
                    <Testimonial
                      key={testimonial.name}
                      variant="stars"
                      className="bg-white"
                      imageURL={testimonial.image}
                      name={testimonial.name}
                      date={testimonial.date}
                      stars={testimonial.stars}
                    >
                      {testimonial.text}
                    </Testimonial>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="order-2 h-fit lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div
              className={cn(
                "flex min-h-[384px] flex-col border border-[#FAF8F8] py-6 md:rounded-[24px] md:bg-white md:p-6 md:shadow-[4px_8px_24px_2px_rgba(82,77,75,0.12)] lg:min-h-[624px]",
                isSubmitting && "items-center justify-center",
                isMounted && size !== "sm" && styles.formContainer,
                isMounted && size === "sm" && styles.formContainerBorderTop
              )}
            >
              {isSubmitting ? (
                <div className="m-auto flex h-full min-h-[400px] items-center justify-center">
                  <Ringer
                    size={60}
                    color="teal"
                    colorShade="500"
                  />
                </div>
              ) : (
                <>
                  <div
                    id={`${id}-section`}
                    className="flex flex-col gap-4"
                  >
                    <h3 className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3">
                      Transform Your Care Facility Today
                    </h3>
                    <p className="font-roboto text-body-1-300 text-neutral-500">
                      All required fields are marked with an asterisk.
                    </p>
                  </div>
                  <form
                    id="ci-app-demo-request-form"
                    className="mt-4 flex flex-col"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {/* Main form inputs with 24px gap */}
                    <div className="flex flex-col gap-4 lg:gap-6">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Input
                          color="white"
                          id="first-name"
                          label="First Name"
                          autoComplete="given-name"
                          required
                          placeholder="First Name *"
                          error={!!errors.firstName}
                          errorMessage={errors.firstName?.message}
                          {...register("firstName")}
                        />

                        <Input
                          color="white"
                          id="last-name"
                          label="Last Name"
                          autoComplete="family-name"
                          required
                          placeholder="Last Name *"
                          error={!!errors.lastName}
                          errorMessage={errors.lastName?.message}
                          {...register("lastName")}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Input
                          color="white"
                          id="email"
                          label="Email Address"
                          type="email"
                          autoComplete="email"
                          required
                          placeholder="Email Address *"
                          error={!!errors.email}
                          errorMessage={errors.email?.message}
                          {...register("email")}
                        />

                        <Input
                          color="white"
                          id="phone"
                          label="Phone Number"
                          autoComplete="tel"
                          required
                          placeholder="Phone Number *"
                          error={!!errors.phoneNumber}
                          errorMessage={errors.phoneNumber?.message}
                          {...register("phoneNumber")}
                        />
                      </div>

                      <div>
                        <Input
                          color="white"
                          id="facility-name"
                          label="Facility Name"
                          required
                          placeholder="Facility Name *"
                          error={!!errors.facilityName}
                          errorMessage={errors.facilityName?.message}
                          {...register("facilityName")}
                        />
                      </div>

                      <div>
                        <Textarea
                          id="message"
                          label="Questions before your demo? (Optional)"
                          rows={3}
                          placeholder="Questions before your demo? (Optional)"
                          error={!!errors.message}
                          errorMessage={errors.message?.message}
                          {...register("message")}
                        />
                      </div>
                    </div>

                    {/* Bottom elements with 16px gap */}
                    <div className="mt-4 flex flex-col gap-4">
                      <div>
                        <p className="font-roboto text-body-1-300 text-neutral-400">
                          By submitting, you agree to Care Indeed&apos;s{" "}
                          <Link
                            href={withUrl(LINKS.termsOfUse.href)}
                            prefetch
                            className="underline"
                          >
                            Terms of Use
                          </Link>{" "}
                          and{" "}
                          <Link
                            href={withUrl(LINKS.privacyPolicy.href)}
                            prefetch
                            className="underline"
                          >
                            Privacy Policy
                          </Link>
                          .
                        </p>
                      </div>

                      {recaptchaKey && (
                        <div>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={recaptchaKey}
                            onChange={handleRecaptchaChange}
                          />
                        </div>
                      )}

                      <div>
                        <PrimaryButton
                          color="teal"
                          size="md"
                          disabled={isSubmitting}
                          type="submit"
                          className="flex w-fit"
                        >
                          Request Demo
                        </PrimaryButton>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CIAppDemoRequestForm;
